// ============================================================================
// MÓDULO 7 - UNIDAD 9: LIKES & DISLIKES (B1→B2)
// ============================================================================
// Nivel: B1-B2 (Progression to Upper-Intermediate)
// Gramática: -ING adjectives (cause) vs -ED adjectives (feeling)
// Modifiers: quite (moderate), really (strong), absolutely (extreme)
// B2 Expressions: keen on, into, can't stand, not my cup of tea, etc.
// ============================================================================

export const M7_UNIT_9 = {
  id: "m7-u9",
  title: "Unit 9: Likes & Dislikes - B1 to B2",
  topic: "Master -ING vs -ED adjectives, use sophisticated modifiers, and express preferences with advanced B2 expressions!",
  
  grammarImage: "/images/grammar/m7-u9-adjectives-modifiers.jpg",
  grammarText: `
    <h3>Expressing Likes & Dislikes - B1 to B2</h3>
    
    <div class="ing-ed-critical">
      <h4>-ING vs -ED Adjectives (Most Common Error!)</h4>
      
      <div class="ing-rule">
        <h5>-ING = Describes the CAUSE/THING:</h5>
        <ul>
          <li>The movie is <strong>boring</strong> (the movie causes boredom)</li>
          <li>The book is <strong>interesting</strong> (the book causes interest)</li>
          <li>The class is <strong>tiring</strong> (the class causes tiredness)</li>
        </ul>
        <p><strong>-ING describes:</strong> what makes you feel something</p>
      </div>
      
      <div class="ed-rule">
        <h5>-ED = Describes the FEELING/PERSON:</h5>
        <ul>
          <li>I am <strong>bored</strong> (I feel boredom)</li>
          <li>She is <strong>interested</strong> (she feels interest)</li>
          <li>We are <strong>tired</strong> (we feel tiredness)</li>
        </ul>
        <p><strong>-ED describes:</strong> how the person feels</p>
      </div>
      
      <div class="contrast">
        <h5>CONTRAST Examples:</h5>
        <ul>
          <li>The movie is bor<strong>ing</strong> → I am bor<strong>ed</strong></li>
          <li>The book is interest<strong>ing</strong> → She is interest<strong>ed</strong></li>
          <li>The game is excit<strong>ing</strong> → They are excit<strong>ed</strong></li>
        </ul>
      </div>
    </div>

    <div class="intensity-modifiers">
      <h4>Intensity Modifiers</h4>
      
      <div class="quite">
        <h5>QUITE (bastante - moderate):</h5>
        <ul>
          <li>It's <strong>quite interesting</strong> (moderately interesting)</li>
          <li>She's <strong>quite good</strong> (fairly good)</li>
          <li>❌ I <strong>quite love</strong> it (WRONG! - too weak for "love")</li>
        </ul>
      </div>
      
      <div class="really">
        <h5>REALLY (muy - strong):</h5>
        <ul>
          <li>It's <strong>really interesting</strong> (very interesting)</li>
          <li>I <strong>really love</strong> it (strong like)</li>
          <li>✅ Works with everything!</li>
        </ul>
      </div>
      
      <div class="absolutely">
        <h5>ABSOLUTELY (absolutamente - extreme):</h5>
        <ul>
          <li>It's <strong>absolutely amazing</strong>! (extreme positive)</li>
          <li>I <strong>absolutely love</strong> it! (extreme like)</li>
          <li>Use with: love, hate, amazing, awful, perfect, terrible</li>
        </ul>
      </div>
    </div>

    <div class="b2-expressions">
      <h4>B2 Advanced Expressions</h4>
      
      <p><strong>Advanced Likes:</strong></p>
      <ul>
        <li><strong>I'm keen on</strong> - interested in</li>
        <li><strong>I'm into</strong> - enthusiastic about</li>
        <li><strong>I'm passionate about</strong> - love intensely</li>
        <li><strong>I'm crazy/mad about</strong> - love extremely</li>
      </ul>
      
      <p><strong>Advanced Dislikes:</strong></p>
      <ul>
        <li><strong>I can't stand</strong> - hate strongly</li>
        <li><strong>It's not my cup of tea</strong> - polite dislike</li>
        <li><strong>It's not really my thing</strong> - not interested</li>
        <li><strong>It drives me crazy</strong> - annoys intensely</li>
      </ul>
    </div>
  `,

  iconType: "heart",

  sets: [
    // ========================================================================
    // SET 1: FOUNDATION - BASIC PREFERENCES 💙
    // ========================================================================
    {
      id: "m7-u9-s1",
      title: "Set 1: Basic Preferences Foundation 💙",
      context: `**Getting to Know You**

New friend: "Tell me about your interests! What do you like?"`,
    
questions: [

  {
        type: "passage",
        passage: `**Getting to Know You

New friend: "Tell me about your interests! What do you like?"`,
      },
        {
          type: "match",
          q: "Match basic preference verbs with their intensity:",
          pairs: [
            { left: "Love", right: "Strongest positive (me encanta)" },
            { left: "Like", right: "Positive (me gusta)" },
            { left: "Don't mind", right: "Neutral/okay (no me importa)" },
            { left: "Hate", right: "Strongest negative (odio)" }
          ],
            a: "completed",
          successtip: "✓ PREFERENCE SCALE!\nLove → Like → Don't mind → Dislike → Hate = intensity scale! Foundation for modifiers! 💙"
        },
        {
          type: "mc",
          q: "You: 'I ___ chocolate ice cream - it's my favorite!'",
          options: [
            "love",
            "hate",
            "dislike"
          ],
          a: "love",
          successtip: "✓ STRONGEST POSITIVE!\n'Love' = strongest like! Favorite = you love it! 🍫💙"
        },
        {
          type: "blank",
          q: "Friend: 'Do you like horror movies?' You: 'Not really, I ___ them.' (opposite of like)",
          a: "dislike",
          accept: { ignoreCase: true, alts: ["don't like", "hate"] },
          successtip: "✓ NEGATIVE PREFERENCE!\n'Dislike' or 'don't like' = negative. 'Hate' = stronger! All work! 👎"
        },
        {
          type: "mc",
          q: "You: 'I ___ spicy food - I can eat it, but it's not my favorite.'",
          options: [
            "don't mind",
            "love",
            "hate"
          ],
          a: "don't mind",
          successtip: "✓ NEUTRAL!\n'Don't mind' = okay with it, not strong feeling. Can tolerate it! 🌶️😐"
        },
        {
          type: "order",
          q: "Traduce: Me encanta viajar a nuevos lugares",
          a: "I love traveling to new places",
          successtip: "✓ PASSION!\n'Love traveling' = strong positive feeling! Travel = common love! ✈️💙"
        },
        {
          type: "mc",
          q: "Friend: 'How about vegetables?' You: 'I ___ them - they're healthy and delicious!'",
          options: [
            "like / love",
            "hate",
            "can't stand"
          ],
          a: "like / love",
          successtip: "✓ POSITIVE ATTITUDE!\n'Like' or 'love' vegetables = healthy choice! Both positive! 🥗💚"
        },
        {
          type: "blank",
          q: "You: 'I absolutely ___ waking up early - it's the worst!' (strongest negative)",
          a: "hate",
          accept: { ignoreCase: true },
          successtip: "✓ STRONGEST DISLIKE!\n'Hate' = strongest negative feeling! Early mornings = common hate! ⏰😫"
        },
        {
          type: "mc",
          q: "You: 'I ___ going to the gym. It's okay, nothing special.'",
          options: [
            "don't mind",
            "absolutely love",
            "really hate"
          ],
          a: "don't mind",
          successtip: "✓ NEUTRAL FEELING!\n'Don't mind' = it's okay, no strong feeling. Not exciting but acceptable! 🏋️😐"
        },
        {
          type: "order",
          q: "Traduce: Odio el tráfico - es muy frustrante",
          a: "I hate traffic - it's very frustrating",
          successtip: "✓ COMMON DISLIKE!\n'Hate traffic' = universal feeling! Traffic = frustrating for everyone! 🚗😤"
        },
        {
          type: "mc",
          q: "Summary: The scale from strongest positive to strongest negative is:",
          options: [
            "Love → Like → Don't mind → Dislike → Hate",
            "Hate → Dislike → Like → Love",
            "All mean the same"
          ],
          a: "Love → Like → Don't mind → Dislike → Hate",
          successtip: "✓ PREFERENCE SPECTRUM!\nThis scale = foundation for adding modifiers (really, absolutely, etc.)! 📊"
        }
      ]
    },

    // ========================================================================
    // SET 2: -ING ADJECTIVES - DESCRIBE THE CAUSE 📺
    // ========================================================================
    {
      id: "m7-u9-s2",
      title: "Set 2: -ING Adjectives - The Cause 📺",
      context: `**Movie Review**

Reviewer: "Let me tell you about the movie I watched..."`,
      
      questions: [
        {
          type: "mc",
          q: "Reviewer: 'The movie was ___.' (The movie causes boredom - describe the movie)",
          options: [
            "boring",
            "bored",
            "bore"
          ],
          a: "boring",
          successtip: "✓ -ING FOR CAUSE!\n'Boring' = the movie causes boredom! -ING describes the THING that creates feeling! 📺"
        },
        {
          type: "blank",
          q: "Reviewer: 'The plot was really ___.' (The plot causes interest - use 'interest' + -ing)",
          a: "interesting",
          accept: { ignoreCase: true },
          successtip: "✓ CAUSES INTEREST!\n'Interesting' = the plot creates interest! -ING = what makes you feel something! 🎬"
        },
        {
          type: "mc",
          q: "Why do we use -ING adjectives?",
          options: [
            "To describe the CAUSE - what creates the feeling",
            "To describe how a person feels",
            "They're interchangeable with -ED"
          ],
          a: "To describe the CAUSE - what creates the feeling",
          successtip: "✓ -ING RULE!\n-ING = describes what CAUSES the feeling! The thing, not the person! 🎯"
        },
        {
          type: "order",
          q: "Traduce: El libro es fascinante (the book causes fascination)",
          a: "The book is fascinating",
          successtip: "✓ BOOK AS CAUSE!\n'Fascinating' (book) creates fascination! -ING describes the book! 📚✨"
        },
        {
          type: "mc",
          q: "Teacher: 'The lesson was ___.' (The lesson causes confusion - describe lesson)",
          options: [
            "confusing",
            "confused",
            "confuse"
          ],
          a: "confusing",
          successtip: "✓ LESSON CAUSES!\n'Confusing' lesson = the lesson creates confusion! -ING for the cause! 📖😵"
        },
        {
          type: "blank",
          q: "Friend: 'The concert was ___!' (The concert causes excitement - use 'excite' + -ing)",
          a: "exciting",
          accept: { ignoreCase: true },
          successtip: "✓ CONCERT ENERGY!\n'Exciting' concert = creates excitement! -ING for the thing that excites! 🎸✨"
        },
        {
          type: "mc",
          q: "Complete: 'The documentary was ___.' (It causes amazement)",
          options: [
            "amazing",
            "amazed",
            "amaze"
          ],
          a: "amazing",
          successtip: "✓ DOCUMENTARY QUALITY!\n'Amazing' documentary = it creates amazement! Describes the documentary! 📺🌟"
        },
        {
          type: "order",
          q: "Traduce: El viaje fue agotador (the trip causes tiredness)",
          a: "The trip was tiring",
          successtip: "✓ EXHAUSTING JOURNEY!\n'Tiring' trip = the trip causes tiredness! -ING for the cause! 🚗😴"
        },
        {
          type: "mc",
          q: "Gamer: 'This game is ___!' (The game causes annoyance)",
          options: [
            "annoying",
            "annoyed",
            "annoy"
          ],
          a: "annoying",
          successtip: "✓ FRUSTRATING GAME!\n'Annoying' game = the game causes annoyance! Describes the game! 🎮😤"
        },
        {
          type: "mc",
          q: "Summary: Use -ING adjectives to describe:",
          options: [
            "The thing/situation that CAUSES the feeling (movie, book, trip)",
            "How the person feels",
            "Only people"
          ],
          a: "The thing/situation that CAUSES the feeling (movie, book, trip)",
          successtip: "✓ -ING = CAUSE!\nBoring movie, interesting book, tiring day = all describe the CAUSE! Next: -ED for feelings! 🎯"
        }
      ]
    },

    // ========================================================================
    // SET 3: COMMON -ING ADJECTIVES ⚡
    // ========================================================================
    {
      id: "m7-u9-s3",
      title: "Set 3: Common -ING Adjectives ⚡",
      context: `**Daily Experiences**

Let's practice describing things that cause different feelings...`,
      
      questions: [
        {
          type: "match",
          q: "Match -ING adjectives with situations that cause those feelings:",
          pairs: [
            { left: "Frightening", right: "Horror movie, scary situation" },
            { left: "Relaxing", right: "Spa, beach, meditation" },
            { left: "Disappointing", right: "Bad result, letdown" },
            { left: "Surprising", right: "Unexpected news, twist" }
          ],
          successtip: "✓ SITUATION DESCRIPTORS!\nAll -ING = describe situations that CAUSE feelings! Context helps! ⚡"
        },
        {
          type: "mc",
          q: "You: 'That horror movie was absolutely ___!' (causes fear)",
          options: [
            "frightening",
            "frightened",
            "fright"
          ],
          a: "frightening",
          successtip: "✓ SCARY MOVIE!\n'Frightening' movie = it causes fear! The movie scares people! 😱🎬"
        },
        {
          type: "blank",
          q: "Friend: 'This massage is so ___.' (causes relaxation - use 'relax' + -ing)",
          a: "relaxing",
          accept: { ignoreCase: true },
          successtip: "✓ PEACEFUL EXPERIENCE!\n'Relaxing' massage = it creates relaxation! Describes the massage! 💆‍♀️😌"
        },
        {
          type: "mc",
          q: "Fan: 'The plot twist was ___!' (causes surprise)",
          options: [
            "surprising",
            "surprised",
            "surprise"
          ],
          a: "surprising",
          successtip: "✓ UNEXPECTED!\n'Surprising' twist = the twist causes surprise! Describes the plot element! 😲📖"
        },
        {
          type: "order",
          q: "Traduce: El final fue decepcionante (the ending causes disappointment)",
          a: "The ending was disappointing",
          successtip: "✓ BAD ENDING!\n'Disappointing' ending = it creates disappointment! Describes the ending! 😞🎬"
        },
        {
          type: "mc",
          q: "Student: 'This math problem is ___.' (causes frustration)",
          options: [
            "frustrating",
            "frustrated",
            "frustrate"
          ],
          a: "frustrating",
          successtip: "✓ DIFFICULT PROBLEM!\n'Frustrating' problem = it causes frustration! Describes the math! 🔢😤"
        },
        {
          type: "blank",
          q: "Tourist: 'The view from here is ___!' (causes awe - use 'amaze' + -ing)",
          a: "amazing",
          accept: { ignoreCase: true },
          successtip: "✓ SPECTACULAR VIEW!\n'Amazing' view = it creates amazement! Beautiful scenery! 🏔️✨"
        },
        {
          type: "mc",
          q: "Worker: 'My job is ___.' (causes exhaustion)",
          options: [
            "tiring / exhausting",
            "tired",
            "tire"
          ],
          a: "tiring / exhausting",
          successtip: "✓ HARD WORK!\n'Tiring/exhausting' job = the job causes tiredness! Describes the work! 💼😴"
        },
        {
          type: "order",
          q: "Traduce: Esta película es entretenida (the movie causes entertainment)",
          a: "This movie is entertaining",
          successtip: "✓ FUN TO WATCH!\n'Entertaining' movie = it entertains you! Enjoyable film! 🎬😊"
        },
        {
          type: "mc",
          q: "Listener: 'This music is ___.' (causes calm feelings)",
          options: [
            "soothing / calming",
            "soothed",
            "soothe"
          ],
          a: "soothing / calming",
          successtip: "✓ PEACEFUL SOUND!\n'Soothing/calming' music = it creates calm! Describes the music! 🎵😌"
        }
      ]
    },

    // ========================================================================
    // SET 4: -ED ADJECTIVES - DESCRIBE THE FEELING 😊
    // ========================================================================
    {
      id: "m7-u9-s4",
      title: "Set 4: -ED Adjectives - The Feeling 😊",
      context: `**Sharing Personal Feelings**

Friend: "How did you feel about the movie?"`,
      
      questions: [
        {
          type: "mc",
          q: "You: 'I was ___ during the movie.' (YOU feel boredom - describe YOUR feeling)",
          options: [
            "bored",
            "boring",
            "bore"
          ],
          a: "bored",
          successtip: "✓ -ED FOR FEELING!\n'Bored' = YOU feel boredom! -ED describes how the PERSON feels! 😴"
        },
        {
          type: "blank",
          q: "Friend: 'I'm really ___ in the story!' (The person feels interest - use 'interest' + -ed)",
          a: "interested",
          accept: { ignoreCase: true },
          successtip: "✓ PERSONAL INTEREST!\n'Interested' = the person feels interest! -ED for the person's feeling! 😊"
        },
        {
          type: "mc",
          q: "Why do we use -ED adjectives?",
          options: [
            "To describe how a PERSON feels",
            "To describe the thing that causes the feeling",
            "They're the same as -ING"
          ],
          a: "To describe how a PERSON feels",
          successtip: "✓ -ED RULE!\n-ED = describes how the PERSON feels! Their emotion, not the cause! 🎯"
        },
        {
          type: "order",
          q: "Traduce: Estoy fascinado por este libro (I feel fascination)",
          a: "I am fascinated by this book",
          successtip: "✓ PERSONAL FEELING!\n'Fascinated' (person) = YOU feel fascination! -ED for your emotion! 📚😍"
        },
        {
          type: "mc",
          q: "Student: 'I was ___ by the lesson.' (The student feels confusion - describe student)",
          options: [
            "confused",
            "confusing",
            "confuse"
          ],
          a: "confused",
          successtip: "✓ STUDENT'S FEELING!\n'Confused' student = the student feels confusion! -ED for person! 😵"
        },
        {
          type: "blank",
          q: "Fan: 'We were so ___ after the concert!' (They feel excitement - use 'excite' + -ed)",
          a: "excited",
          accept: { ignoreCase: true },
          successtip: "✓ FAN EMOTION!\n'Excited' fans = they feel excitement! -ED for their feeling! 🎸😆"
        },
        {
          type: "mc",
          q: "Complete: 'She was ___ by the documentary.' (She feels amazement)",
          options: [
            "amazed",
            "amazing",
            "amaze"
          ],
          a: "amazed",
          successtip: "✓ HER REACTION!\n'Amazed' = she feels amazement! -ED describes her emotion! 😲✨"
        },
        {
          type: "order",
          q: "Traduce: Estamos cansados después del viaje (we feel tiredness)",
          a: "We are tired after the trip",
          successtip: "✓ EXHAUSTED FEELING!\n'Tired' = we feel tiredness! -ED for our feeling, not the trip! 😴"
        },
        {
          type: "mc",
          q: "Gamer: 'I'm so ___ with this game!' (The person feels annoyance)",
          options: [
            "annoyed",
            "annoying",
            "annoy"
          ],
          a: "annoyed",
          successtip: "✓ PLAYER'S FRUSTRATION!\n'Annoyed' = the person feels annoyance! -ED for the gamer! 🎮😤"
        },
        {
          type: "mc",
          q: "Summary: Use -ED adjectives to describe:",
          options: [
            "How the PERSON feels (bored, interested, tired)",
            "The thing that causes the feeling",
            "Only objects and situations"
          ],
          a: "How the PERSON feels (bored, interested, tired)",
          successtip: "✓ -ED = FEELING!\nBored person, interested student, tired worker = all describe PEOPLE's feelings! Next: contrast! 🎯"
        }
      ]
    },

    // ========================================================================
    // SET 5: COMMON -ED ADJECTIVES 💭
    // ========================================================================
    {
      id: "m7-u9-s5",
      title: "Set 5: Common -ED Adjectives 💭",
      context: `**Personal Experiences**

Describing how YOU and others feel in different situations...`,
      
      questions: [
        {
          type: "match",
          q: "Match -ED adjectives with personal feelings:",
          pairs: [
            { left: "Frightened", right: "Person feels fear" },
            { left: "Relaxed", right: "Person feels calm" },
            { left: "Disappointed", right: "Person feels letdown" },
            { left: "Surprised", right: "Person feels shock" }
          ],
            a: "completed",
          successtip: "✓ PERSONAL EMOTIONS!\nAll -ED = describe how PEOPLE feel! Their internal states! 💭"
        },
        {
          type: "mc",
          q: "You: 'I was so ___ during the horror movie!' (YOU feel fear)",
          options: [
            "frightened",
            "frightening",
            "fright"
          ],
          a: "frightened",
          successtip: "✓ YOUR FEAR!\n'Frightened' = YOU feel fear! -ED for your emotion during movie! 😱"
        },
        {
          type: "blank",
          q: "Friend: 'I feel so ___ after the massage.' (The person feels relaxation - use 'relax' + -ed)",
          a: "relaxed",
          accept: { ignoreCase: true },
          successtip: "✓ PERSONAL CALM!\n'Relaxed' = the person feels calm! -ED for their peaceful state! 😌💆"
        },
        {
          type: "mc",
          q: "Reader: 'I was ___ by the plot twist!' (Reader feels surprise)",
          options: [
            "surprised",
            "surprising",
            "surprise"
          ],
          a: "surprised",
          successtip: "✓ READER'S REACTION!\n'Surprised' = reader feels surprise! -ED for the person's shock! 😲"
        },
        {
          type: "order",
          q: "Traduce: Estaba decepcionado con el final (I felt disappointment)",
          a: "I was disappointed with the ending",
          successtip: "✓ YOUR DISAPPOINTMENT!\n'Disappointed' = YOU felt letdown! -ED for your emotion! 😞"
        },
        {
          type: "mc",
          q: "Student: 'I'm ___ with this math problem.' (Student feels frustration)",
          options: [
            "frustrated",
            "frustrating",
            "frustrate"
          ],
          a: "frustrated",
          successtip: "✓ STUDENT'S FRUSTRATION!\n'Frustrated' = student feels frustration! -ED for their feeling! 😤📐"
        },
        {
          type: "blank",
          q: "Tourist: 'I'm ___ by this view!' (Tourist feels awe - use 'amaze' + -ed)",
          a: "amazed",
          accept: { ignoreCase: true },
          successtip: "✓ TOURIST'S AWE!\n'Amazed' = tourist feels amazement! -ED for their wonder! 😍🏔️"
        },
        {
          type: "mc",
          q: "Worker: 'I'm so ___ after work.' (Worker feels exhaustion)",
          options: [
            "tired / exhausted",
            "tiring",
            "tire"
          ],
          a: "tired / exhausted",
          successtip: "✓ WORKER'S FATIGUE!\n'Tired/exhausted' = worker feels tiredness! -ED for their state! 😴💼"
        },
        {
          type: "order",
          q: "Traduce: Los niños están emocionados por la fiesta (they feel excitement)",
          a: "The children are excited about the party",
          successtip: "✓ CHILDREN'S EXCITEMENT!\n'Excited' = they feel excitement! -ED for kids' emotion! 🎉😆"
        },
        {
          type: "mc",
          q: "Listener: 'I feel ___ by this music.' (Person feels calm)",
          options: [
            "soothed / calmed",
            "soothing",
            "soothe"
          ],
          a: "soothed / calmed",
          successtip: "✓ PERSONAL PEACE!\n'Soothed/calmed' = person feels calm! -ED for their feeling! 🎵😌"
        }
      ]
    },

    // ========================================================================
    // SET 6: CRITICAL CONTRAST - -ING VS -ED ⚠️
    // ========================================================================
    {
      id: "m7-u9-s6",
      title: "Set 6: -ING vs -ED - THE BIG DIFFERENCE ⚠️",
      context: `**Most Common English Error!**

Master the difference between -ING (cause) and -ED (feeling)...`,
      
      questions: [
        {
          type: "mc",
          q: "Complete BOTH: 'The movie is ___' (describe movie) + 'so I am ___' (describe feeling)",
          options: [
            "boring / bored",
            "bored / boring",
            "Both the same"
          ],
          a: "boring / bored",
          successtip: "✓ PERFECT CONTRAST!\nMovie IS boring (cause) → I AM bored (feeling)! -ING vs -ED! 📺😴"
        },
        {
          type: "blank",
          q: "Fix error: 'I am boring at home.' → 'I am ___ at home.'",
          a: "bored",
          accept: { ignoreCase: true },
          successtip: "✓ COMMON ERROR FIXED!\n'I am bored' (feeling)! 'I am boring' = YOU are uninteresting person! ❌→✅"
        },
        {
          type: "mc",
          q: "Complete: 'The book is ___' (cause) → 'She is ___' (feeling)",
          options: [
            "interesting / interested",
            "interested / interesting",
            "Both the same"
          ],
          a: "interesting / interested",
          successtip: "✓ BOOK & READER!\nBook IS interesting (cause) → She IS interested (feeling)! Perfect pair! 📚😊"
        },
        {
          type: "order",
          q: "Fix both: 'The game is excited and we are exciting' → 'The game is ___ and we are ___'",
          a: "exciting and we are excited",
          successtip: "✓ DOUBLE CORRECTION!\nGame IS exciting (cause) + We ARE excited (feeling)! Both fixed! 🎮✅"
        },
        {
          type: "mc",
          q: "Which means 'YOU make people bored'?",
          options: [
            "I am boring (I cause boredom in others!)",
            "I am bored (I feel boredom)",
            "Both mean the same"
          ],
          a: "I am boring (I cause boredom in others!)",
          successtip: "✓ PERSONALITY DESCRIPTION!\n'I am boring' = your personality is boring! 'I am bored' = you feel bored! Big difference! ⚠️"
        },
        {
          type: "blank",
          q: "Complete: 'The lecture is ___' (cause) → 'The students are ___' (feeling). Use 'confuse'",
          a: "confusing, confused",
          accept: { ignoreCase: true, alts: ["confusing / confused"] },
          successtip: "✓ CLASSROOM SCENARIO!\nLecture IS confusing (cause) → Students ARE confused (feeling)! Clear contrast! 📖😵"
        },
        {
          type: "mc",
          q: "Complete: 'The concert is ___' (cause) → 'The fans are ___' (feeling)",
          options: [
            "exciting / excited",
            "excited / exciting",
            "Both work either way"
          ],
          a: "exciting / excited",
          successtip: "✓ EVENT & PEOPLE!\nConcert IS exciting (cause) → Fans ARE excited (feeling)! -ING vs -ED! 🎸😆"
        },
        {
          type: "order",
          q: "Traduce: La película es aburrida, por eso estoy aburrido (both forms!)",
          a: "The movie is boring, so I am bored",
          successtip: "✓ CAUSE & EFFECT!\nCause (boring movie) → Effect (bored person)! Complete contrast! 🎬😴"
        },
        {
          type: "mc",
          q: "Error check: 'The news was shocked.' What's wrong?",
          options: [
            "Should be 'shocking' - news is the cause, not a person with feelings",
            "Nothing wrong",
            "Should be 'shock'"
          ],
          a: "Should be 'shocking' - news is the cause, not a person with feelings",
          successtip: "✓ NEWS = CAUSE!\nNews IS shocking (causes shock)! News can't FEEL shocked! Only people feel! 📰⚠️"
        },
        {
          type: "mc",
          q: "CRITICAL RULE: -ING describes ___, -ED describes ___:",
          options: [
            "The CAUSE (thing/situation) / The FEELING (person)",
            "The feeling / The cause",
            "They're interchangeable"
          ],
          a: "The CAUSE (thing/situation) / The FEELING (person)",
          successtip: "✓ MASTER RULE!\n-ING = CAUSE (boring movie) ✅\n-ED = FEELING (bored person) ✅\nMost important grammar point! 🎯⚠️"
        }
      ]
    },

    // ========================================================================
    // SET 7: INTENSITY MODIFIERS 🎚️
    // ========================================================================
    {
      id: "m7-u9-s7",
      title: "Set 7: Intensity Modifiers - Quite, Really, Absolutely 🎚️",
      context: `**Expressing Intensity**

Learning to show HOW MUCH you like or dislike something...`,
      
      questions: [
        {
          type: "match",
          q: "Match modifiers with their intensity levels:",
          pairs: [
            { left: "Quite", right: "Moderate/fairly (bastante)" },
            { left: "Really", right: "Strong (muy)" },
            { left: "Absolutely", right: "Extreme (absolutamente)" },
            { left: "Very", right: "Strong (muy)" }
          ],
            a: "completed",
          successtip: "✓ INTENSITY SCALE!\nQuite < Really/Very < Absolutely! Shows degrees of feeling! 🎚️"
        },
        {
          type: "mc",
          q: "You: 'The movie was ___ good - not amazing, but decent.' (moderate intensity)",
          options: [
            "quite",
            "absolutely",
            "extremely"
          ],
          a: "quite",
          successtip: "✓ MODERATE PRAISE!\n'Quite good' = fairly good, not extreme. Moderate positive! 🎬👍"
        },
        {
          type: "blank",
          q: "You: 'I ___ love this song!' (strong intensity - use common modifier)",
          a: "really",
          accept: { ignoreCase: true, alts: ["very", "absolutely"] },
          successtip: "✓ STRONG FEELING!\n'Really/absolutely love' = strong! 'Really' = most common for love! 🎵💙"
        },
        {
          type: "mc",
          q: "You: 'This pizza is ___ delicious!' (extreme intensity)",
          options: [
            "absolutely",
            "quite",
            "fairly"
          ],
          a: "absolutely",
          successtip: "✓ EXTREME PRAISE!\n'Absolutely delicious' = extreme positive! Strongest intensity! 🍕✨"
        },
        {
          type: "order",
          q: "Which is WRONG? 'I quite love it' or 'I really love it'?",
          a: "I quite love it",
          successtip: "✓ CRITICAL ERROR!\n'Quite' is TOO WEAK for 'love'! ❌ Use 'really' or 'absolutely' with love! ✅"
        },
        {
          type: "mc",
          q: "Which is INCORRECT?",
          options: [
            "I quite love chocolate (wrong - quite too weak!)",
            "I really love chocolate",
            "I absolutely love chocolate"
          ],
          a: "I quite love chocolate (wrong - quite too weak!)",
          successtip: "✓ MODIFIER MISMATCH!\n'Quite' = moderate, 'love' = extreme. Don't match! ❌ Use really/absolutely! ⚠️"
        },
        {
          type: "blank",
          q: "You: 'The book was ___ interesting.' (moderate - fairly interesting)",
          a: "quite",
          accept: { ignoreCase: true, alts: ["fairly", "pretty"] },
          successtip: "✓ MODERATE INTEREST!\n'Quite interesting' = fairly interesting, not super! Moderate level! 📚"
        },
        {
          type: "mc",
          q: "You: 'I ___ hate waiting in lines!' (strong/extreme - both work)",
          options: [
            "really / absolutely",
            "quite",
            "fairly"
          ],
          a: "really / absolutely",
          successtip: "✓ STRONG DISLIKE!\n'Really/absolutely hate' = both work! Strong negative! ⏰😤"
        },
        {
          type: "order",
          q: "Traduce: La comida es absolutamente perfecta (extreme modifier)",
          a: "The food is absolutely perfect",
          successtip: "✓ EXTREME PRAISE!\n'Absolutely perfect' = extreme words go together! Maximum intensity! 🍽️✨"
        },
        {
          type: "mc",
          q: "'Quite' can be used with:",
          options: [
            "Moderate adjectives (good, interesting, nice) - NOT extreme verbs",
            "Love and hate",
            "Everything equally"
          ],
          a: "Moderate adjectives (good, interesting, nice) - NOT extreme verbs",
          successtip: "✓ 'QUITE' LIMITATION!\n'Quite' = moderate words only! ✅ quite good ✅ quite interesting ❌ quite love ❌ quite hate"
        }
      ]
    },

    // ========================================================================
    // SET 8: B2 ADVANCED EXPRESSIONS 🎯
    // ========================================================================
    {
      id: "m7-u9-s8",
      title: "Set 8: B2 Advanced Expressions - Likes 🎯",
      context: `**Sophisticated English**

Moving from B1 to B2 with advanced expressions...`,
      
      questions: [
        {
          type: "match",
          q: "Match B2 expressions with their meanings:",
          pairs: [
            { left: "I'm keen on", right: "I'm interested in / I like" },
            { left: "I'm into", right: "I'm enthusiastic about" },
            { left: "I can't stand", right: "I hate strongly" },
            { left: "Not my cup of tea", right: "Not my preference (polite)" }
          ],
            a: "completed",
          successtip: "✓ B2 VOCABULARY!\nThese = native-level expressions! More sophisticated than 'like/dislike'! 🎯"
        },
        {
          type: "mc",
          q: "You: 'I'm really ___ photography.' (B2 expression for 'interested in')",
          options: [
            "keen on",
            "boring about",
            "tired of"
          ],
          a: "keen on",
          successtip: "✓ B2 EXPRESSION!\n'Keen on' = interested in, enthusiastic about! More sophisticated than 'I like'! 📷✨"
        },
        {
          type: "blank",
          q: "You: 'I'm really ___ yoga these days.' (B2: enthusiastic about - casual)",
          a: "into",
          accept: { ignoreCase: true },
          successtip: "✓ CASUAL B2!\n'Into' = enthusiastic about! Common in casual speech! 'Really into yoga' = love it! 🧘‍♀️"
        },
        {
          type: "mc",
          q: "You: 'I ___ horror movies - they're too scary!' (B2 strong dislike)",
          options: [
            "can't stand",
            "quite like",
            "am interested in"
          ],
          a: "can't stand",
          successtip: "✓ STRONG DISLIKE!\n'Can't stand' = hate strongly! B2 alternative to 'hate'! More expressive! 😱❌"
        },
        {
          type: "order",
          q: "Traduce: No es mi gusto (polite B2 expression - use 'cup of tea')",
          a: "It's not my cup of tea",
          successtip: "✓ POLITE BRITISH EXPRESSION!\n'Not my cup of tea' = polite way to say you don't like something! Very British! ☕🇬🇧"
        },
        {
          type: "mc",
          q: "You: 'I'm ___ about environmental issues.' (B2: love intensely)",
          options: [
            "passionate",
            "bored",
            "tired"
          ],
          a: "passionate",
          successtip: "✓ STRONG COMMITMENT!\n'Passionate about' = love intensely, care deeply! B2 sophistication! 🌍💚"
        },
        {
          type: "blank",
          q: "You: 'I'm absolutely ___ about football!' (B2: love extremely - informal)",
          a: "crazy",
          accept: { ignoreCase: true, alts: ["mad"] },
          successtip: "✓ EXTREME ENTHUSIASM!\n'Crazy/mad about' = love extremely! Informal but B2! ⚽😍"
        },
        {
          type: "mc",
          q: "You: 'I have a ___ for romantic comedies.' (B2: special affection)",
          options: [
            "soft spot",
            "hard time",
            "bad habit"
          ],
          a: "soft spot",
          successtip: "✓ TENDER FEELING!\n'Have a soft spot for' = special affection/weakness for! Sweet B2 expression! 🎬💕"
        },
        {
          type: "order",
          q: "Traduce: Soy muy aficionado a la música clásica (use 'fond of')",
          a: "I am very fond of classical music",
          successtip: "✓ FORMAL B2!\n'Fond of' = like with affection! Slightly formal, very B2! 🎵✨"
        },
        {
          type: "mc",
          q: "Complete B2: 'Spicy food? ___' (polite way to say 'not interested')",
          options: [
            "It's not really my thing",
            "I absolutely love it",
            "I'm passionate about it"
          ],
          a: "It's not really my thing",
          successtip: "✓ POLITE DISINTEREST!\n'Not really my thing' = polite way to decline! B2 alternative to 'don't like'! 🌶️😐"
        }
      ]
    },

    // ========================================================================
    // SET 9: MORE B2 EXPRESSIONS 💬
    // ========================================================================
    {
      id: "m7-u9-s9",
      title: "Set 9: More B2 Expressions - Advanced 💬",
      context: `**Native-Level Expressions**

Master these to sound like a native speaker...`,
      
      questions: [
        {
          type: "mc",
          q: "You: 'Loud music ___ when I'm trying to concentrate.' (B2: annoys intensely)",
          options: [
            "drives me crazy",
            "makes me happy",
            "is quite nice"
          ],
          a: "drives me crazy",
          successtip: "✓ INTENSE ANNOYANCE!\n'Drives me crazy/mad' = annoys me intensely! B2 expression! 🔊😤"
        },
        {
          type: "blank",
          q: "You: 'I could ___ or ___ it.' (B2: indifferent - two-word phrase)",
          a: "take, leave",
          accept: { ignoreCase: true, alts: ["take / leave", "take it or leave it"] },
          successtip: "✓ INDIFFERENCE!\n'Could take it or leave it' = indifferent, don't care either way! B2 neutral! 😐"
        },
        {
          type: "mc",
          q: "You: 'At first I didn't like this song, but it ___.' (B2: becomes better over time)",
          options: [
            "grows on you",
            "drives you crazy",
            "stands out"
          ],
          a: "grows on you",
          successtip: "✓ CHANGING OPINION!\n'Grows on you' = becomes better with time! Classic B2 expression! 🎵⏰"
        },
        {
          type: "order",
          q: "Traduce: Es un gusto adquirido (B2 expression - some things need time to appreciate)",
          a: "It's an acquired taste",
          successtip: "✓ SOPHISTICATED EXPRESSION!\n'Acquired taste' = something that needs time to appreciate! Wine, coffee, etc! 🍷☕"
        },
        {
          type: "mc",
          q: "You: 'I'm not really ___ spicy food.' (B2: don't like much)",
          options: [
            "keen on",
            "passionate about",
            "crazy about"
          ],
          a: "keen on",
          successtip: "✓ MILD DISLIKE!\n'Not keen on' = don't like much! More sophisticated than 'don't like'! 🌶️"
        },
        {
          type: "blank",
          q: "You: 'I'm totally ___ superhero movies right now!' (B2 casual: enthusiastic)",
          a: "into",
          accept: { ignoreCase: true },
          successtip: "✓ CURRENT ENTHUSIASM!\n'Totally into' = very enthusiastic RIGHT NOW! Casual B2! 🦸‍♂️🎬"
        },
        {
          type: "mc",
          q: "You: 'I absolutely ___ waiting in traffic!' (B2 strong dislike)",
          options: [
            "can't stand / hate",
            "love",
            "enjoy"
          ],
          a: "can't stand / hate",
          successtip: "✓ TRAFFIC FRUSTRATION!\n'Can't stand' = B2 sophisticated hate! Universal feeling! 🚗😤"
        },
        {
          type: "order",
          q: "Traduce: Tengo debilidad por el chocolate (use 'soft spot')",
          a: "I have a soft spot for chocolate",
          successtip: "✓ SWEET TOOTH!\n'Have a soft spot for' = special weakness/affection! Perfect for food! 🍫💕"
        },
        {
          type: "mc",
          q: "You: 'Classical music? ___ to be honest.' (B2 polite disinterest)",
          options: [
            "It's not really my thing",
            "I'm crazy about it",
            "I'm passionate about it"
          ],
          a: "It's not really my thing",
          successtip: "✓ POLITE DECLINE!\n'Not really my thing' = polite way to show disinterest! B2 politeness! 🎵😐"
        },
        {
          type: "mc",
          q: "Complete: 'Reality TV? I ___.' (B2: neutral/indifferent)",
          options: [
            "could take it or leave it",
            "absolutely love it",
            "can't stand it"
          ],
          a: "could take it or leave it",
          successtip: "✓ NO STRONG OPINION!\n'Could take it or leave it' = completely indifferent! B2 neutrality! 📺😐"
        }
      ]
    },

    // ========================================================================
    // SET 10: COMPLETE B2 MASTERY 🌟
    // ========================================================================
    {
      id: "m7-u9-s10",
      title: "Set 10: Complete B2 Mastery 🌟",
      context: `**FINAL CHALLENGE**

You're having a sophisticated conversation about preferences. Use ALL Unit 9 concepts: -ing/-ed, modifiers, and B2 expressions!`,
      
      questions: [
        {
          type: "mc",
          q: "Friend: 'How was the movie?' You: 'It was absolutely ___!' (movie = cause)",
          options: [
            "amazing",
            "amazed",
            "amaze"
          ],
          a: "amazing",
          successtip: "✓ -ING CAUSE!\nMovie IS amazing (cause)! -ING describes the movie! Plus 'absolutely' = extreme modifier! 🎬✨"
        },
        {
          type: "blank",
          q: "Friend: 'And how did you feel?' You: 'I was absolutely ___!' (feeling - use 'amaze' + -ed)",
          a: "amazed",
          accept: { ignoreCase: true },
          successtip: "✓ -ED FEELING!\nI WAS amazed (feeling)! -ED describes YOU! Contrast with -ING! 😍"
        },
        {
          type: "mc",
          q: "Friend: 'Do you like jazz?' You: 'Honestly, ___.' (B2 polite dislike)",
          options: [
            "it's not my cup of tea / it's not really my thing",
            "I absolutely love it",
            "I'm passionate about it"
          ],
          a: "it's not my cup of tea / it's not really my thing",
          successtip: "✓ POLITE B2 DISLIKE!\nBoth expressions = sophisticated, polite way to decline! B2 level! 🎵😐"
        },
        {
          type: "order",
          q: "Traduce: Estoy muy entusiasmado con la fotografía últimamente (B2: use 'into')",
          a: "I am really into photography lately",
          successtip: "✓ CURRENT INTEREST!\n'Really into' = B2 enthusiasm! 'Lately' = recent interest! 📷✨"
        },
        {
          type: "mc",
          q: "Friend: 'Why don't you like it?' You: 'The plot was ___ so I got ___.' (-ing + -ed contrast!)",
          options: [
            "confusing / confused",
            "confused / confusing",
            "Both same"
          ],
          a: "confusing / confused",
          successtip: "✓ PERFECT CONTRAST!\nPlot WAS confusing (cause) → I GOT confused (feeling)! -ING→-ED chain! 📖😵"
        },
        {
          type: "mc",
          q: "Which is WRONG?",
          options: [
            "I quite love chocolate (quite too weak for love!)",
            "I really love chocolate",
            "I absolutely love chocolate"
          ],
          a: "I quite love chocolate (quite too weak for love!)",
          successtip: "✓ MODIFIER ERROR!\n'Quite' = moderate, can't use with extreme 'love'! Use really/absolutely! ⚠️🍫"
        },
        {
          type: "blank",
          q: "You: 'I absolutely ___ horror movies!' (B2 strong dislike - two words)",
          a: "can't stand",
          accept: { ignoreCase: true, alts: ["can't / stand", "hate"] },
          successtip: "✓ B2 STRONG DISLIKE!\n'Can't stand' = sophisticated alternative to 'hate'! B2 expression! 😱❌"
        },
        {
          type: "mc",
          q: "Friend: 'Traffic ___ during rush hour!' (B2: annoys intensely)",
          options: [
            "drives me crazy",
            "is quite nice",
            "grows on me"
          ],
          a: "drives me crazy",
          successtip: "✓ B2 ANNOYANCE!\n'Drives me crazy' = intense annoyance! Native expression! 🚗😤"
        },
        {
          type: "order",
          q: "Error fix: 'I am boring when I have nothing to do' → 'I am ___ when I have nothing to do'",
          a: "bored",
          successtip: "✓ CRITICAL CORRECTION!\n'I am bored' (feeling)! 'I am boring' = your personality is dull! ❌→✅"
        },
        {
          type: "mc",
          q: "FINAL MASTERY: Which shows COMPLETE Unit 9 mastery?",
          options: [
            "'The movie was absolutely amazing, so I was amazed! I'm really into sci-fi, though horror is not my cup of tea because it's too frightening and I get frightened easily.'",
            "'The movie was absolutely amazed, so I was amazing! I'm quite love sci-fi, though horror is my cup of tea because it's too frightened.'",
            "'The movie was boring and I was boring. I love sci-fi and horror both same.'"
          ],
          a: "'The movie was absolutely amazing, so I was amazed! I'm really into sci-fi, though horror is not my cup of tea because it's too frightening and I get frightened easily.'",
          successtip: "🎉 UNIT 9 COMPLETELY MASTERED! 🎉\n\n✅ -ING ADJECTIVES (CAUSE)\n• Describes what CAUSES feeling ✅\n• Amazing, frightening, confusing, boring ✅\n• 'The movie is boring' (movie causes boredom) ✅\n\n✅ -ED ADJECTIVES (FEELING)\n• Describes how PERSON feels ✅\n• Amazed, frightened, confused, bored ✅\n• 'I am bored' (I feel boredom) ✅\n\n✅ CRITICAL CONTRAST\n• Boring movie → Bored person ✅\n• Frightening movie → Frightened person ✅\n• -ING (cause) vs -ED (feeling) = mastered! ✅\n\n✅ INTENSITY MODIFIERS\n• QUITE: moderate (quite good, quite interesting) ✅\n• REALLY: strong (really love, really good) ✅\n• ABSOLUTELY: extreme (absolutely amazing, absolutely love) ✅\n• ERROR AVOIDED: NOT 'quite love' ✅\n\n✅ B2 EXPRESSIONS - LIKES\n• I'm keen on (interested in) ✅\n• I'm into (enthusiastic about) ✅\n• I'm passionate about (love intensely) ✅\n• I'm crazy/mad about (extreme love) ✅\n• I have a soft spot for (special affection) ✅\n\n✅ B2 EXPRESSIONS - DISLIKES\n• I can't stand (hate strongly) ✅\n• It's not my cup of tea (polite dislike) ✅\n• It's not really my thing (not interested) ✅\n• It drives me crazy (annoys intensely) ✅\n\n✅ B2 EXPRESSIONS - NEUTRAL\n• I could take it or leave it (indifferent) ✅\n• It grows on you (gets better with time) ✅\n• It's an acquired taste (needs time) ✅\n\n✅ CORRECT IN FINAL SENTENCE:\n• 'Absolutely amazing' (extreme + -ing) ✅\n• 'I was amazed' (-ed feeling) ✅\n• 'Really into' (B2 + strong modifier) ✅\n• 'Not my cup of tea' (B2 polite dislike) ✅\n• 'Too frightening' (-ing cause) ✅\n• 'I get frightened' (-ed feeling) ✅\n\n🌟 YOU NOW SOUND B2 LEVEL!\n• Sophisticated expressions ✅\n• Perfect -ing/-ed usage ✅\n• Natural modifiers ✅\n• Native-like preferences ✅\n\nB1→B2 PROGRESSION = COMPLETE! 💪\n\n¡Excelente trabajo! Ready for reading! 📖🚀"
        }
      ]
    }
  ],

  // ==========================================================================
  // READING SET - OPINIONS ABOUT EVERYTHING (5 PASSAGES OF ~40 WORDS)
  // ==========================================================================
  readingSet: {
    id: "m7-u9-reading",
    title: "Reading Practice - Opinions About Everything",
    category: "reading",
    
    questions: [
      // ======================================================================
      // BLOQUE 1: Movies & Books (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Opinions About Everything**

**Part 1: Movie Review**

I watched a thriller last night. The movie was absolutely fascinating but quite frightening. I was fascinated by the plot twists but frightened during the scary scenes. The ending was surprising - I was completely surprised!`
      },
      {
        type: "mc",
        q: "How many -ING/-ED pairs appear?",
        options: [
          "Three: fascinating/fascinated, frightening/frightened, surprising/surprised",
          "Only one",
          "None"
        ],
        a: "Three: fascinating/fascinated, frightening/frightened, surprising/surprised",
        successtip: "✓ PERFECT CONTRASTS!\nAll three pairs show -ING (cause) vs -ED (feeling)! Perfect usage! 🎬"
      },
      {
        type: "blank",
        q: "'The movie was fascinating' vs 'I was fascinated' - which describes the CAUSE?",
        a: "The movie was fascinating",
        accept: { ignoreCase: true, alts: ["fascinating", "the movie", "movie was fascinating"] },
        successtip: "✓ -ING = CAUSE!\n'Fascinating' describes the movie (cause)! -ING adjective! 📺"
      },

      // ======================================================================
      // BLOQUE 2: Travel Experiences (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 2: Travel Blog**

My trip to Iceland was absolutely amazing. The landscapes were really breathtaking. I was completely amazed by the Northern Lights. The volcano tour was quite tiring, and I was exhausted afterward. Still, it was easily the best trip!`
      },
      {
        type: "mc",
        q: "Which modifiers appear?",
        options: [
          "All three: absolutely, really, quite",
          "Only 'very'",
          "None"
        ],
        a: "All three: absolutely, really, quite",
        successtip: "✓ ALL MODIFIERS!\n'Absolutely' (extreme), 'really' (strong), 'quite' (moderate) = all used correctly! 🎚️"
      },
      {
        type: "mc",
        q: "'The tour was tiring' vs 'I was exhausted' - correct -ing/-ed usage?",
        options: [
          "Yes - tiring (cause), exhausted (feeling)",
          "No - should be reversed",
          "Both should be -ing"
        ],
        a: "Yes - tiring (cause), exhausted (feeling)",
        successtip: "✓ PERFECT PAIR!\nTour IS tiring (cause) → I WAS exhausted (feeling)! Correct! 🚶‍♂️😴"
      },

      // ======================================================================
      // BLOQUE 3: Food Opinions (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 3: Food Preferences**

I'm really into Japanese cuisine. I absolutely love sushi! Spicy food? It's not really my cup of tea. I can't stand overly spicy dishes. Sour flavors? I could take them or leave them. Coffee is an acquired taste!`
      },
      {
        type: "mc",
        q: "How many B2 expressions are used?",
        options: [
          "Five: into, not my cup of tea, can't stand, take it or leave it, acquired taste",
          "Only two",
          "None - all basic"
        ],
        a: "Five: into, not my cup of tea, can't stand, take it or leave it, acquired taste",
        successtip: "✓ B2 RICH!\nAll five = sophisticated B2 expressions! Native-level vocabulary! 🍣✨"
      },
      {
        type: "blank",
        q: "Which expression means 'indifferent'?",
        a: "could take it or leave it",
        accept: { ignoreCase: true, alts: ["take it or leave it", "could take them or leave them"] },
        successtip: "✓ NEUTRAL EXPRESSION!\n'Could take it or leave it' = don't care either way! B2 indifference! 😐"
      },

      // ======================================================================
      // BLOQUE 4: Hobbies & Interests (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 4: Hobbies**

I'm passionate about photography. I'm quite keen on hiking too. Video games? Not really my thing, though they're growing on me. I have a soft spot for board games. I'm absolutely crazy about traveling - it's my true passion!`
      },
      {
        type: "mc",
        q: "Which B2 expressions show POSITIVE feelings?",
        options: [
          "All: passionate about, keen on, have a soft spot for, crazy about",
          "None show positive",
          "Only 'keen on'"
        ],
        a: "All: passionate about, keen on, have a soft spot for, crazy about",
        successtip: "✓ POSITIVE B2!\nAll four = sophisticated ways to express likes! B2 variety! 📷🎲✈️"
      },
      {
        type: "mc",
        q: "'It's growing on me' means:",
        options: [
          "It's becoming better over time / I'm starting to like it",
          "It's getting worse",
          "I always hated it"
        ],
        a: "It's becoming better over time / I'm starting to like it",
        successtip: "✓ CHANGING OPINION!\n'Grows on you' = gets better with time! Classic B2 expression! ⏰💙"
      },

      // ======================================================================
      // BLOQUE 5: Pet Peeves + FINAL (~40 words)
      // ======================================================================
      {
        type: "passage",
        passage: `**Part 5: What I Dislike**

Loud chewing absolutely drives me crazy - it's so annoying! I'm annoyed easily by it. I can't stand people who are late. Rude behavior? It's really frustrating, and I get frustrated quickly. Noisy environments are exhausting - I feel exhausted!`
      },
      {
        type: "mc",
        q: "How many -ing/-ed contrasts appear?",
        options: [
          "Three: annoying/annoyed, frustrating/frustrated, exhausting/exhausted",
          "Only one",
          "None"
        ],
        a: "Three: annoying/annoyed, frustrating/frustrated, exhausting/exhausted",
        successtip: "✓ TRIPLE CONTRAST!\nAll three pairs show perfect -ING (cause) vs -ED (feeling)! Mastered! 🎯"
      },
      {
        type: "mc",
        q: "Which B2 expressions for strong dislike appear?",
        options: [
          "Two: drives me crazy, can't stand",
          "None",
          "Only basic 'hate'"
        ],
        a: "Two: drives me crazy, can't stand",
        successtip: "✓ STRONG DISLIKES!\n'Drives me crazy' + 'can't stand' = both B2 sophisticated dislikes! 😤"
      },
      {
        type: "mc",
        q: "Throughout ALL passages, what did you notice?",
        options: [
          "All Unit 9 concepts: -ing/-ed contrast, modifiers (quite/really/absolutely), B2 expressions",
          "Only basic vocabulary",
          "No special patterns"
        ],
        a: "All Unit 9 concepts: -ing/-ed contrast, modifiers (quite/really/absolutely), B2 expressions",
        successtip: "🎉 READING COMPLETE! 🎉\n\n📚 YOU SAW IN CONTEXT:\n\n✅ -ING ADJECTIVES (CAUSE)\n• Fascinating, frightening, surprising ✅\n• Annoying, frustrating, exhausting ✅\n• All describe what CAUSES feeling ✅\n\n✅ -ED ADJECTIVES (FEELING)\n• Fascinated, frightened, surprised ✅\n• Annoyed, frustrated, exhausted ✅\n• All describe how PERSON feels ✅\n\n✅ PERFECT CONTRASTS\n• Fascinating movie → I was fascinated ✅\n• Annoying sound → I'm annoyed ✅\n• Every pair correctly used! ✅\n\n✅ INTENSITY MODIFIERS\n• Absolutely (extreme): absolutely amazing ✅\n• Really (strong): really breathtaking ✅\n• Quite (moderate): quite tiring ✅\n\n✅ B2 EXPRESSIONS - ALL USED!\n• I'm into, passionate about, keen on ✅\n• Not my cup of tea, not my thing ✅\n• Can't stand, drives me crazy ✅\n• Have a soft spot for, crazy about ✅\n• Take it or leave it, acquired taste ✅\n• Growing on me ✅\n\n🌟 UNIT 9 IN ACTION!\n-ING/-ED + Modifiers + B2 expressions = Sophisticated English! 💪📖"
      }
    ]
  },

  // ==========================================================================
  // REVIEW SET - UNIT 9 INTEGRATION (COMPLETO)
  // ==========================================================================
  reviewSet: {
    id: "m7-u9-review",
    title: "⭐ Unit 9 Review: Likes & Dislikes (B1→B2)",
    category: "review",
    isReview: true,
    
    questions: [
      {
        type: "mc",
        q: "Which is CORRECT?",
        options: [
          "The movie is boring (movie causes boredom)",
          "The movie is bored (movie feels bored)",
          "Both mean the same"
        ],
        a: "The movie is boring (movie causes boredom)",
        successtip: "✓ -ING = CAUSE!\nMovie IS boring = it causes boredom! -ING describes the thing! 📺"
      },
      
      {
        type: "blank",
        q: "Fix error: 'I am boring at home' → 'I am ___ at home'",
        a: "bored",
        accept: { ignoreCase: true },
        successtip: "✓ COMMON ERROR!\n'I am bored' (feeling)! 'I am boring' = you're a dull person! ❌→✅"
      },
      
      {
        type: "mc",
        q: "Which modifier is TOO WEAK for 'love'?",
        options: [
          "Quite (too weak!)",
          "Really",
          "Absolutely"
        ],
        a: "Quite (too weak!)",
        successtip: "✓ MODIFIER ERROR!\n'Quite' = moderate, can't use with extreme 'love'! Use really/absolutely! ⚠️"
      },
      
      {
        type: "order",
        q: "Traduce: No es mi gusto - de manera educada (use B2 expression)",
        a: "It's not my cup of tea",
        successtip: "✓ POLITE B2!\n'Not my cup of tea' = sophisticated polite decline! Very British! ☕🇬🇧"
      },
      
      {
        type: "mc",
        q: "B2 expression for 'I hate strongly':",
        options: [
          "I can't stand",
          "I quite like",
          "I'm keen on"
        ],
        a: "I can't stand",
        successtip: "✓ STRONG DISLIKE!\n'Can't stand' = sophisticated alternative to 'hate'! B2 level! 😤"
      },
      
      {
        type: "blank",
        q: "Complete B2: 'I'm really ___ photography.' (enthusiastic about)",
        a: "into",
        accept: { ignoreCase: true, alts: ["keen on"] },
        successtip: "✓ B2 ENTHUSIASM!\n'Into' or 'keen on' = both B2 expressions for enthusiasm! 📷✨"
      },
      
      {
        type: "mc",
        q: "'The lesson is confusing' vs 'I am confused' - which describes the feeling?",
        options: [
          "I am confused (person's feeling)",
          "The lesson is confusing",
          "Both the same"
        ],
        a: "I am confused (person's feeling)",
        successtip: "✓ -ED = FEELING!\n'I am confused' = how YOU feel! -ED for person! 😵"
      },
      
      {
        type: "order",
        q: "Traduce: Me vuelve loco cuando llega tarde (use 'drives me crazy')",
        a: "It drives me crazy when he/she is late",
        successtip: "✓ B2 ANNOYANCE!\n'Drives me crazy' = B2 expression for intense annoyance! 😤⏰"
      },
      
      {
        type: "mc",
        q: "Intensity scale from weakest to strongest:",
        options: [
          "Quite → Really → Absolutely",
          "Absolutely → Really → Quite",
          "All equal"
        ],
        a: "Quite → Really → Absolutely",
        successtip: "✓ MODIFIER SCALE!\nQuite (moderate) < Really (strong) < Absolutely (extreme)! 🎚️"
      },
      
      {
        type: "mc",
        q: "FINAL: Complete Unit 9 mastery means:",
        options: [
          "Using -ING for cause, -ED for feeling, correct modifiers, and B2 expressions naturally",
          "Only knowing -ing/-ed",
          "Just using 'like' and 'hate'"
        ],
        a: "Using -ING for cause, -ED for feeling, correct modifiers, and B2 expressions naturally",
        successtip: "🎉 UNIT 9 MASTERED - B1→B2! 🎉\n\n✅ -ING/-ED ADJECTIVES\n• -ING describes CAUSE (boring movie) ✅\n• -ED describes FEELING (bored person) ✅\n• Most common error = MASTERED! ✅\n\n✅ INTENSITY MODIFIERS\n• Quite: moderate (quite good) ✅\n• Really: strong (really love) ✅\n• Absolutely: extreme (absolutely amazing) ✅\n• ERROR: Don't use 'quite' with love/hate! ✅\n\n✅ B2 EXPRESSIONS - LIKES\n• I'm keen on, I'm into ✅\n• I'm passionate about ✅\n• I'm crazy/mad about ✅\n• I have a soft spot for ✅\n\n✅ B2 EXPRESSIONS - DISLIKES\n• I can't stand ✅\n• It's not my cup of tea ✅\n• It's not really my thing ✅\n• It drives me crazy ✅\n\n✅ B2 EXPRESSIONS - NEUTRAL\n• I could take it or leave it ✅\n• It grows on you ✅\n• It's an acquired taste ✅\n\n🌟 B2 LEVEL ACHIEVED!\n• Sophisticated vocabulary ✅\n• Perfect -ing/-ed usage ✅\n• Natural modifiers ✅\n• Native-like expressions ✅\n\nB1→B2 COMPLETE! 💪🎓\n\n¡Excelente! Ready for Unit 10! 🚀"
      }
    ]
  }
};
